import { useState, useEffect } from 'react'
import AdminSidebar from '../../components/admin/AdminSidebar'
import './AdminTestimonialsPage.css'
import { apiFetch } from '../../utils/api'

// const API = process.env.REACT_APP_API_URL || 'http://localhost:8000'

const STATUS_STYLES = {
  pending:  { bg: 'rgba(255,200,80,0.1)',  border: 'rgba(255,200,80,0.3)',  text: '#ffc850' },
  approved: { bg: 'rgba(120,220,160,0.1)', border: 'rgba(120,220,160,0.3)', text: '#78dca0' },
  rejected: { bg: 'rgba(255,80,80,0.08)',  border: 'rgba(255,80,80,0.25)',  text: '#ff8080' },
}

function StarDisplay({ rating }) {
  return (
    <div className='at-stars'>
      {[1,2,3,4,5].map(s => (
        <span key={s} className={`at-star ${rating >= s ? 'at-star--on' : ''}`}>★</span>
      ))}
    </div>
  )
}

function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-KE', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function initials(name) {
  return name?.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?'
}

// ── Review card ───────────────────────────────────────────────────
function ReviewCard({ t, onStatusChange, onDelete }) {
  const [loading, setLoading] = useState(false)
  const s = STATUS_STYLES[t.status] || STATUS_STYLES.pending

  const updateStatus = async (status) => {
    setLoading(true)
    try {
      await apiFetch(`/api/admin/testimonials/${t.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      })
      onStatusChange(t.id, status)
    } finally { setLoading(false) }
  }

  const handleDelete = async () => {
    if (!window.confirm(`Delete review from ${t.name}? This cannot be undone.`)) return
    setLoading(true)
    try {
      await apiFetch(`/api/admin/testimonials/${t.id}`, { method: 'DELETE' })
      onDelete(t.id)
    } finally { setLoading(false) }
  }

  return (
    <div className={`at-card at-card--${t.status}`}>
      <div className='at-card-top'>
        {/* Person */}
        <div className='at-person'>
          <div className='at-avatar'>{initials(t.name)}</div>
          <div className='at-meta'>
            <strong>{t.name}</strong>
            <span>{[t.role, t.company].filter(Boolean).join(', ') || '—'}</span>
          </div>
        </div>

        {/* Right: rating + status badge */}
        <div className='at-card-right'>
          <StarDisplay rating={t.rating} />
          <span className='at-badge' style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.text }}>
            {t.status.charAt(0).toUpperCase() + t.status.slice(1)}
          </span>
        </div>
      </div>

      {/* Review text */}
      <p className='at-review'>"{t.review}"</p>

      {/* Project link if any */}
      {t.project_link && (
        <a href={t.project_link} target='_blank' rel='noreferrer' className='at-project-link'>
          ↗ {t.project_label || t.project_link}
        </a>
      )}

      {/* Footer */}
      <div className='at-card-footer'>
        <span className='at-date'>{formatDate(t.submitted_at)}</span>
        <div className='at-actions'>
          {t.status !== 'approved' && (
            <button className='at-btn at-btn--approve' onClick={() => updateStatus('approved')} disabled={loading}>
              ✓ Approve
            </button>
          )}
          {t.status !== 'rejected' && (
            <button className='at-btn at-btn--reject' onClick={() => updateStatus('rejected')} disabled={loading}>
              ✕ Reject
            </button>
          )}
          {t.status !== 'pending' && (
            <button className='at-btn at-btn--pending' onClick={() => updateStatus('pending')} disabled={loading}>
              ↺ Pending
            </button>
          )}
          <button className='at-btn at-btn--delete' onClick={handleDelete} disabled={loading}>
            🗑 Delete
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────
export default function AdminTestimonialsPage() {
  const [reviews, setReviews]   = useState([])
  const [loading, setLoading]   = useState(true)
  const [error, setError]       = useState(null)
  const [filter, setFilter]     = useState('all')

  const load = async () => {
    setLoading(true)
    try {
      const res  = await apiFetch('/api/admin/testimonials')
      const data = await res.json()
      setReviews(data.testimonials || [])
    } catch { setError('Could not load reviews.') }
    finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  const handleStatusChange = (id, status) => {
    setReviews(prev => prev.map(r => r.id === id ? { ...r, status } : r))
  }

  const handleDelete = (id) => {
    setReviews(prev => prev.filter(r => r.id !== id))
  }

  const counts = {
    all: reviews.length,
    pending:  reviews.filter(r => r.status === 'pending').length,
    approved: reviews.filter(r => r.status === 'approved').length,
    rejected: reviews.filter(r => r.status === 'rejected').length,
  }

  const filtered = filter === 'all' ? reviews : reviews.filter(r => r.status === filter)

  return (
    <div className='at-shell'>
      <AdminSidebar counts={{ total: reviews.length }} />

      <main className='at-main'>
        <div className='at-topbar'>
          <div>
            <h1 className='at-title'>Testimonials</h1>
            <p className='at-sub'>{filtered.length} of {reviews.length} reviews</p>
          </div>
        </div>

        {/* Filter bar */}
        <div className='at-filter-bar'>
          {[
            ['all',      'All',      null],
            ['pending',  'Pending',  '#ffc850'],
            ['approved', 'Approved', '#78dca0'],
            ['rejected', 'Rejected', '#ff8080'],
          ].map(([key, label, color]) => (
            <button
              key={key}
              className={`at-filter-btn ${filter === key ? 'at-filter-btn--active' : ''}`}
              onClick={() => setFilter(key)}
            >
              {color && <span className='at-filter-dot' style={{ background: color }}></span>}
              {label}
              <span className='at-filter-count'>{counts[key]}</span>
            </button>
          ))}
        </div>

        {loading && <div className='at-state'><div className='at-spinner'></div><p>Loading…</p></div>}
        {error   && <div className='at-state at-state--error'><p>⚠ {error}</p></div>}

        {!loading && !error && filtered.length === 0 && (
          <div className='at-state'>
            <p>{filter !== 'all' ? `No ${filter} reviews.` : 'No reviews submitted yet.'}</p>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className='at-list'>
            {filtered.map(t => (
              <ReviewCard
                key={t.id}
                t={t}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  )
}